import type {
  Equipment,
  EquipmentType,
} from "@src/global_functions/backpackSdk/equipment/types";
import { UnitType } from "@src/global_functions/backpackSdk/tenantsAndSpaces/unitTypes";
import {
  FloorWithSpaces,
  Space,
} from "@src/global_functions/backpackSdk/tenantsAndSpaces/util";
import { LegacyFloorWithSpaces } from "@src/global_functions/fortyTwoApi/tenantsAndSpaces/hooks/useGetFloors";
import {
  RentableSpace,
  SupportedRentableSpaceTypes,
} from "@src/global_functions/fortyTwoApi/tenantsAndSpaces/types";
import { sumBySafe } from "@src/global_functions/util";
import { atom, useAtom } from "jotai";
import { chain, groupBy } from "lodash";
import moment from "moment";
import * as R from "ramda";
import { useCallback, useMemo, useState } from "react";
import { useIsOpenAtom } from "./Multifamily/multifamilySidePanelOpenState";
import { supportedRentableSpaceTypes as supportedOfficeRentableSpaceTypes } from "./Office/TenantsAndSpaces/supportedRentableSpaceTypes";
import { ParkingSpaceFormType } from "./Office/TenantsAndSpaces/types";
import {
  spaceTypeOptions as officeSpaceTypeOptions,
  tenantsAndSpacesColorMap,
} from "./Office/TenantsAndSpaces/utils";
import { useTenantsAndSpacesContext } from "./Office/TenantsAndSpacesContext";
import { FloorState, SpaceWithEquipment } from "./types";

export const activeBreakdownSectionIdAtom = atom<number | string | null>(null);
export const useActiveBreakdownSectionIdAtom = () =>
  useAtom(activeBreakdownSectionIdAtom);

const selectedSpaceIdAtom = atom<number | null>(null);
export const useSelectedSpaceIdAtom = () => useAtom(selectedSpaceIdAtom);

const selectedFloorIdAtom = atom<number | null>(null);
export const useSelectedFloorIdAtom = () => useAtom(selectedFloorIdAtom);

export function useActiveFloorAreaInput(floorIds: (number | string)[]) {
  const [activeInput, setActiveInput] = useState<number | string | null>(null);
  const setActiveNextRentableAreaInput = useCallback(
    (direction: "downFloor" | "upFloor") => {
      const directionVector = direction === "upFloor" ? -1 : 1;
      if (activeInput) {
        const selectedFloorIndex = floorIds.indexOf(Number(activeInput));
        const nextFloorIndex = selectedFloorIndex + directionVector;
        const isNextInRange =
          nextFloorIndex >= 0 && nextFloorIndex <= floorIds.length - 1;

        setActiveInput(isNextInRange ? floorIds[nextFloorIndex]! : null);
      }
    },
    [activeInput, floorIds]
  );

  const toggleActiveInput = useCallback(
    (floorId: number | string) =>
      floorId === activeInput ? setActiveInput(null) : setActiveInput(floorId),
    [setActiveInput, activeInput]
  );

  return useMemo(
    () => ({ activeInput, toggleActiveInput, setActiveNextRentableAreaInput }),
    [activeInput, setActiveNextRentableAreaInput, toggleActiveInput]
  );
}

export const useOfficeVariantSelectionProps = () => {
  const [, setActiveBreakdownSectionId] = useActiveBreakdownSectionIdAtom();
  const { selectedSpace, selectedFloor, setSelected } =
    useTenantsAndSpacesContext();

  const onClickSpace = useCallback(
    (floorId: string | number) => (spaceId: string | number) => {
      setActiveBreakdownSectionId(spaceId);
      setSelected({
        spaceId,
        floorId,
      });
    },
    [setActiveBreakdownSectionId, setSelected]
  );

  const onClickAddSpace = useCallback(
    (floorId: number) => {
      setSelected({ floorId });
    },
    [setSelected]
  );

  return useMemo(
    () => ({
      selectedSpaceId: selectedSpace?.id ?? null,
      selectedFloorId: selectedFloor?.id ?? null,
      onClickSpace,
      onClickAddSpace,
    }),
    [onClickAddSpace, onClickSpace, selectedFloor?.id, selectedSpace?.id]
  );
};

export const useMultifamilyVariantSelectionProps = () => {
  const [, setActiveBreakdownSectionId] = useActiveBreakdownSectionIdAtom();
  const [, setIsOpen] = useIsOpenAtom();
  const [selectedSpaceId, setSelectedSpaceId] = useSelectedSpaceIdAtom();
  const [selectedFloorId, setSelectedFloorId] = useSelectedFloorIdAtom();
  const onClickSpace = useCallback(
    (floorId: string | number) => (spaceId: string | number) => {
      setActiveBreakdownSectionId(spaceId);
      if (typeof spaceId === "number") {
        setSelectedSpaceId(spaceId);
        setIsOpen(true);
      }
      if (typeof floorId === "number") setSelectedFloorId(floorId);
    },
    [
      setActiveBreakdownSectionId,
      setIsOpen,
      setSelectedFloorId,
      setSelectedSpaceId,
    ]
  );

  const onClickAddSpace = useCallback(
    (floorId: number) => {
      setIsOpen(true);
      setSelectedFloorId(floorId);
    },
    [setIsOpen, setSelectedFloorId]
  );

  return useMemo(
    () => ({
      selectedSpaceId,
      selectedFloorId,
      onClickSpace,
      onClickAddSpace,
    }),
    [onClickAddSpace, onClickSpace, selectedFloorId, selectedSpaceId]
  );
};

export type SelectionProps = ReturnType<typeof useOfficeVariantSelectionProps>;

export const joinEquipmentToSpaces = ({
  siteEquipment,
  isEquipmentLoading,
  floors,
}: {
  siteEquipment?: Equipment[];
  isEquipmentLoading?: boolean;
  floors?: FloorWithSpaces[];
}) => {
  if (isEquipmentLoading) return;
  return floors?.map((floor) => ({
    ...floor,
    spaces: floor.spaces.map((space) => ({
      ...space,
      linkedEquipment: (space.linkedEquipment ?? [])
        .map((equipmentId) =>
          siteEquipment?.find((equipment) => equipment.id === equipmentId)
        )
        .filter((equipment): equipment is Equipment => Boolean(equipment)),
    })),
  }));
};

type ParkingFloorProperties = {
  spansMultipleFloors: boolean;
  topFloorId: number;
  bottomFloorId: number;
};

export type SpaceFormValues = Space &
  ParkingFloorProperties & { space: Pick<Space, "linkedEquipment"> | null };

export type AllSpaceVariants = SpaceFormValues | SpaceWithEquipment;

const supportedRentableSpaceTypes = [
  "MultiFamily",
  "SupermarketGroceryStore",
  ...supportedOfficeRentableSpaceTypes,
] as const satisfies Readonly<Space["spaceType"][]>;

export const isRentableSpace = (space: Partial<AllSpaceVariants>) =>
  supportedRentableSpaceTypes.some(
    (spaceType) => spaceType === space.spaceType
  );

export const spaceTypeOptions: {
  id: Space["spaceType"];
  label: string;
  spaceTypeLabel: string;
}[] = [
  {
    id: "MultiFamily",
    label: "Multifamily Housing",
    spaceTypeLabel: "Rentable Space",
  } as const,
  ...[
    ...officeSpaceTypeOptions,
    {
      id: "SupermarketGroceryStore",
      label: "Supermarket/​Grocery Store", // <-- there is a zero width space after the slash for breaking
      spaceTypeLabel: "Rentable Space",
    } as const,
  ].sort((a, b) => {
    if (a.id === "Other") return 1;
    if (b.id === "Other") return -1;
    return a.id.localeCompare(b.id);
  }),
  {
    id: "Pool",
    label: "Pool",
    spaceTypeLabel: "Non-Rentable Space",
  },
];
export const spaceTypeLabelMap = R.fromPairs(
  R.map(
    ([key, [val]]) => [key, val] as const,
    R.toPairs(R.groupBy(R.prop("id"), spaceTypeOptions))
  )
);

export const multifamilySpaceTypesColorsMap = {
  ...tenantsAndSpacesColorMap,
  // space types not supported in Office
  MultiFamily: "#287382",
  SupermarketGroceryStore: "#CB4547",
  Pool: "#1559C4",
} as const;

const leaseExpirationColorPalette = [
  "#F956A6",
  "#22B03B",
  "#253E69",
  "#F3A93E",
  "#5D2E8C",
  "#B7241D",
  "#1F67D9",
  "#0F9B9F", // 8 or longer years go in last bucket
] as const;

export const unitTypeColorPalette = [
  "#C5F5FF",
  "#D6EBFE",
  "#7EB6EB",
  "#5E8EBA",
  "#4878A5",
  "#285782",
  "#BBD9DF",
  "#71C9DA",
  "#00BAE0",
  "#58A5B5",
  "#408E9E",
  "#287382", // remaining unit types go in "Other" bucket
] as const;

export const UNLEASED_KEY = "Unleased" as const;
export const UNLEASED_COLOR = "#BBBBBB" as const;
export const DEFAULT_COLOR = "#EAEAEA" as const;

export const darkTextBackgroundColors = [
  "#C5F5FF",
  "#D6EBFE",
  "#FFC700",
  "#D6ECF8",
  "#F956A6",
  "#F3A93E",
  DEFAULT_COLOR,
];

// colors were chosen to minimize overlap within each building
export const equipmentColorPaletteMap = {
  AirConditioner: "#913F63",
  AirHandlingUnit: "#549A94",
  AirSourceHeatPump: "#BD5555",
  Boiler: "#61CBC2",
  CentralAirConditioner: "#43CCB7",
  Chiller: "#000000",
  ClothesDryer: "#B2F0DA",
  ClothesWasher: "#41D6A2",
  CombinedClothesWasherDryer: "#5EA28A",
  CookingRange: "#A06BA1",
  Cooktop: "#FB94D2",
  CoolingTower: "#78C0FF",
  Dishwasher: "#FCA8A8",
  DuctlessMiniSplitAirConditioner: "#FCA439",
  EnergyRecoveryVentilationUnit: "#6A856A",
  FanCoilUnit: "#8284C5",
  Furnace: "#BBBBBB",
  GroundSourceHeatPump: "#9F7386",
  HeatExchanger: "#FF7070",
  OutdoorAirHandlingUnit: "#756971",
  Other: "#F2D45E",
  Oven: "#F98CA0",
  PackagedTerminalAirConditioner: "#B4D9EF",
  Pump: "#6DA74A",
  Refrigerator: "#6E68BB",
  RoofTopUnit: "#F7A35D",
  SelfContainedUnit: "#913F63",
  TankWaterHeater: "#3A6D99",
  TanklessWaterHeater: "#000676",
  VentilationFan: "#A6A028",
  WaterSourceHeatPump: "#926D6D",
  WindowUnit: "#CB9D6C",
} satisfies Record<EquipmentType, `#${string}`>;

export type LeaseExpirationColorMap = ReturnType<
  typeof getLeaseExpirationColorMap
>;

export type UnitTypeColorMap = ReturnType<typeof getUnitTypeColorMap>;

export function getLeaseExpirationYears(
  floors: (FloorWithSpaces | FloorState)[]
) {
  const [leased, unleased] = R.partition(
    ([key]) => key !== UNLEASED_KEY,
    R.toPairs(
      R.groupBy(
        R.prop("category"),
        floors.flatMap(({ spaces }) =>
          spaces
            .map(({ linkedEquipment, ...space }) => space)
            .filter(
              (space) =>
                !!space.leaseEndDate ||
                (!space.isSpaceLeased && isRentableSpace(space))
            )
            .map(({ leaseEndDate, floorArea }) => ({
              category: leaseEndDate
                ? getYearString(leaseEndDate)
                : UNLEASED_KEY,
              floorArea: floorArea ?? 0,
            }))
        )
      )
    ).map(
      ([key, spaces]) =>
        [
          key,
          {
            count: spaces.length,
            floorAreaSum: R.sum(R.map(R.prop("floorArea"), spaces)),
          },
        ] as const
    )
  );

  const spacesByYear = R.sort((a, b) => Number([a]) - Number([b]), leased);

  const numYearsDisplayed = leaseExpirationColorPalette.length;
  const yearsToDisplay = [...new Array(numYearsDisplayed)].map((_, i) =>
    moment().add(i, "year").format("YYYY")
  );

  const spacesByYearMap = R.fromPairs(spacesByYear);
  const filledDisplayedYears = yearsToDisplay.map(
    (year) =>
      [year, spacesByYearMap[year] ?? { count: 0, floorAreaSum: 0 }] as const
  );

  const lastDisplayedYear =
    filledDisplayedYears[filledDisplayedYears.length - 1]?.[0];

  const totalsOutsideArray = spacesByYear.reduce(
    (acc, [year, { count, floorAreaSum }]) => {
      if (Number(year) > Number(lastDisplayedYear)) {
        acc.count += count;
        acc.floorAreaSum += floorAreaSum;
      }
      return acc;
    },
    { count: 0, floorAreaSum: 0 }
  );

  const [lastYear, lastYearTotals] = filledDisplayedYears.pop()!;
  const lastDisplayedYearWithOutsideTotals = [
    `${lastYear}+`,
    {
      count: lastYearTotals.count + totalsOutsideArray.count,
      floorAreaSum:
        lastYearTotals.floorAreaSum + totalsOutsideArray.floorAreaSum,
    },
  ] as const;
  return [
    ...filledDisplayedYears,
    lastDisplayedYearWithOutsideTotals,
    ...unleased,
  ];
}

function getYearString(date?: string | null) {
  return `${moment(date).year()}`;
}

export const parseFormattedYear = (formattedYear?: string) =>
  Number(formattedYear?.replace("+", ""));

export function getLeaseExpirationColorForYear(formattedKey: string) {
  if (formattedKey === UNLEASED_KEY) return UNLEASED_COLOR;
  return leaseExpirationColorPalette[
    parseFormattedYear(formattedKey) % leaseExpirationColorPalette.length
  ]!;
}

export function getLeaseExpirationColorMap(floors: FloorState[]) {
  return Object.fromEntries(
    getLeaseExpirationYears(floors).map(([year]) => {
      return [year, getLeaseExpirationColorForYear(year)];
    })
  );
}

export const ALL_OTHER_UNIT_TYPES_KEY = "-1";
export function getUnitTypeCounts(
  floors: (FloorWithSpaces | FloorState)[],
  unitTypes?: UnitType[]
) {
  const spacesWithUnitTypeData = floors.flatMap(({ spaces }) =>
    spaces
      .map(({ linkedEquipment, ...space }) => space)
      .filter(
        (space): space is Omit<Space, "unitType"> & { unitType: number } =>
          typeof space.unitType === "number"
      )
      .map(({ unitType, floorArea: spaceFloorArea }) => {
        const { name, floorArea } =
          unitTypes?.find((unit) => unit.id === unitType) ?? {};
        return {
          category: unitType,
          name,
          unitTypeFloorArea: floorArea,
          spaceFloorArea: spaceFloorArea ?? 0,
        };
      })
  );

  const groupedByUnitType = R.groupBy(
    ({ category }) => String(category),
    spacesWithUnitTypeData
  );

  const spacesByUnitType = R.sortWith(
    [
      ([_, { count: a }], [__, { count: b }]) => b - a,
      ([_, { floorAreaSum: a }], [__, { floorAreaSum: b }]) => b - a,
      ([a], [b]) => a.localeCompare(b),
    ],
    R.toPairs(R.countBy(R.prop("category"), spacesWithUnitTypeData))
      .map(([id, count]) => {
        const units = groupedByUnitType[id];
        if (!units) return null;
        const floorAreaSum = R.sum(
          R.map(R.prop("spaceFloorArea"), units ?? [])
        );
        const unitName = units[0]?.name;
        if (!unitName) return null;
        return [
          unitName,
          {
            floorAreaSum,
            count,
            id,
          },
        ];
      })
      .filter(
        (
          summary
        ): summary is [
          string,
          {
            floorAreaSum: number;
            count: number;
            id: string;
          }
        ] => Boolean(summary)
      )
  );

  const numUnitTypesDisplayed = unitTypeColorPalette.length;
  if (spacesByUnitType.length <= numUnitTypesDisplayed) return spacesByUnitType;

  const withinArray = spacesByUnitType.slice(0, numUnitTypesDisplayed - 1);
  const outsideOfArray = spacesByUnitType.slice(numUnitTypesDisplayed - 1);
  const totalOutsideArrayCount = R.sum(
    R.map(([, { count }]) => count, outsideOfArray)
  );

  const totalOutsideArrayFloorArea = R.sum(
    R.map(([, { floorAreaSum }]) => floorAreaSum, outsideOfArray)
  );

  const allOtherUnitTypes = [
    "All Others",
    {
      count: totalOutsideArrayCount,
      floorAreaSum: totalOutsideArrayFloorArea,
      id: ALL_OTHER_UNIT_TYPES_KEY,
    },
  ] as const;
  return [...withinArray, allOtherUnitTypes];
}

export function getUnitTypeColorMap(
  floors: (FloorWithSpaces | FloorState)[],
  unitTypes?: UnitType[]
) {
  return Object.fromEntries(
    getUnitTypeCounts(floors, unitTypes).map(
      ([name, { id }], i) =>
        [id, { color: unitTypeColorPalette[i], unitName: name }] as const
    )
  );
}

/**
 * Method for approximating rounded numbers that will add up to 100 or between 99-100:
 * Use Math.round() for improved accuracy
 *
 * If any rounded totalSpaceTypeArea percentages = 0:
 * The largest space type area = 99 - sum(all other space types)
 * totalSpaceType percentages that = 0, return as 0 and the component will display them as <1
 *
 * If no rounded totalSpaceTypeArea percentages = 0:
 * Largest term = 100 - sum(all other terms)
 */
export function getSpacesStats(
  rentableSpaces: (Space | RentableSpace | SpaceWithEquipment)[]
) {
  if (rentableSpaces.length === 0) return [];

  const spaceTypeGroups = groupBy(rentableSpaces, (i) => i.spaceType);
  const totalFloorArea = sumBySafe(rentableSpaces, "floorArea");

  const areaBySpaceType = Object.entries(spaceTypeGroups).reduce<
    Array<{
      name: SupportedRentableSpaceTypes;
      percent: number;
      sqrFt: number;
    }>
  >((acc, [spaceType, space]) => {
    const totalSpaceTypeArea = sumBySafe(space, "floorArea");
    const roundedPercentage = Math.round(
      (totalSpaceTypeArea / totalFloorArea) * 100
    );
    acc.push({
      name: spaceType as SupportedRentableSpaceTypes,
      sqrFt: totalSpaceTypeArea,
      percent: roundedPercentage,
    });
    return acc;
  }, []);

  const hasAnyTotalSpaceTypeAreaThatRoundsToZero = areaBySpaceType.some(
    ({ percent }) => percent === 0
  );
  const areaBySpaceTypeDescending = [...areaBySpaceType].sort(
    (a, b) => b.sqrFt - a.sqrFt
  );
  const largestSpaceType = areaBySpaceTypeDescending[0]!;
  const allOtherSpaceTypes = areaBySpaceTypeDescending.slice(1);
  const allOtherSpaceTypesTotalPercentage = sumBySafe(
    allOtherSpaceTypes,
    "percent"
  );

  const allSpaceTypesWithCalculatedPercentage = [
    {
      ...largestSpaceType,
      percent:
        (hasAnyTotalSpaceTypeAreaThatRoundsToZero ? 99 : 100) -
        allOtherSpaceTypesTotalPercentage,
    },
    ...allOtherSpaceTypes,
  ];

  // handle edge case where second largest space is assigned higher percentage due to subtraction
  return allSpaceTypesWithCalculatedPercentage.map((space, i, arr) => {
    if (i === 0 && arr[1]?.percent! > space.percent) {
      return {
        ...space,
        percent: arr[1]!.percent,
      };
    }
    if (i === 1 && arr[0]?.percent! < space.percent) {
      return {
        ...space,
        percent: arr[0]!.percent,
      };
    }
    return { ...space, percent: space.percent };
  });
}

export function getOccupancyStats(
  rentableSpaces: (Space | RentableSpace | SpaceWithEquipment)[]
) {
  const categoryGroups = groupBy(
    rentableSpaces,
    (i) => i.isSpaceLeased === true
  );
  const totalFloorArea = sumBySafe(rentableSpaces, "floorArea");

  return chain(categoryGroups)
    .map((spaces, category) => {
      const categoryArea = sumBySafe(spaces, "floorArea");
      const name: "Leased" | "Unleased" =
        category === "true" ? "Leased" : "Unleased";
      return {
        name,
        percent: parseFloat(((categoryArea / totalFloorArea) * 100).toFixed(1)),
        sqrFt: categoryArea,
      };
    })
    .orderBy("sqrFt", "desc")
    .value();
}

type SharedSpaceFields = Pick<Space, "spaceType"> & {
  floors: (string | number)[];
  id: string | number;
};

export function addBoundingFloorProperties<T extends SharedSpaceFields>(
  space: T,
  floors?: (LegacyFloorWithSpaces | FloorWithSpaces)[],
  { floorIdsOnly }: { floorIdsOnly: boolean } = { floorIdsOnly: false }
) {
  const spansMultipleFloors = space.floors.length > 1;

  if (space.floors.length <= 0) return space;

  if (!spansMultipleFloors) return { ...space, spansMultipleFloors };

  const { topFloor, bottomFloor } = findBoundingFloors(space, floors);
  return {
    ...space,
    spansMultipleFloors,
    ...(topFloor && !floorIdsOnly
      ? {
          topFloor: {
            id: topFloor.id,
            position: topFloor.position,
          },
        }
      : {}),
    ...(topFloor && floorIdsOnly
      ? {
          topFloorId: topFloor.id,
        }
      : {}),
    ...(bottomFloor && !floorIdsOnly
      ? {
          bottomFloor: {
            id: bottomFloor.id,
            position: bottomFloor.position,
          },
        }
      : {}),
    ...(bottomFloor && floorIdsOnly
      ? {
          bottomFloorId: bottomFloor.id,
        }
      : {}),
  };
}

function findBoundingFloors<T extends SharedSpaceFields>(
  space: T,
  floors?: (LegacyFloorWithSpaces | FloorWithSpaces | FloorState)[]
) {
  const includedFloors = floors
    ?.filter((floor) => floor.id && space.floors.includes(floor.id))
    .sort((a, b) => b.position - a.position);
  return {
    topFloor: includedFloors?.[0],
    bottomFloor: includedFloors?.[includedFloors.length - 1],
  };
}

export const generateParkingFloorDropdownOptions = (
  floors?: (LegacyFloorWithSpaces | FloorWithSpaces)[]
) =>
  (floors ?? [])
    .filter((floor) => !floor.isRoof)
    .map((floor) => ({
      id: floor.id,
      label: floor.name,
      position: floor.position,
    }));

export const getLinkedFloors = (
  { bottomFloor, topFloor }: ParkingSpaceFormType,
  floors?: (LegacyFloorWithSpaces | FloorWithSpaces)[]
) => {
  if (!bottomFloor || !topFloor) {
    return [];
  }
  return (floors ?? [])
    .filter(
      (f) =>
        f.position <= topFloor.position && f.position >= bottomFloor?.position
    )
    .map((f) => f.id);
};

export const getBasePath = (siteId: number) =>
  `/site/${siteId}/property/site-overview`;
